// Kay & Burton Bayside detail-page parser. KB's pages have:
//   <title>Kay & Burton | <address></title>
//   <div class="price">$ 1,460,000</div>     (space after $)
//   <ul class="rooms"><li class="bedroom">...4</li><li class="bathroom">...2</li><li class="carspace">...2</li></ul>
// The URL also encodes property type as the second-last path segment
// (.../residential/townhouse/<id>).

const PRICE_RE = /<div\s+class=["']price["'][^>]*>\s*\$\s*([\d,]+(?:\.\d+)?)\s*</i;
const BEDROOM_RE = /<li\s+class=["']bedroom["'][^>]*>[\s\S]*?(\d+)\s*<\/li>/i;
const BATHROOM_RE = /<li\s+class=["']bathroom["'][^>]*>[\s\S]*?(\d+)\s*<\/li>/i;
const CARSPACE_RE = /<li\s+class=["']carspace["'][^>]*>[\s\S]*?(\d+)\s*<\/li>/i;

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function extractAddress(html: string): string | null {
  const title = html.match(/<title[^>]*>([^<]+)<\/title>/i);
  if (!title) return null;
  const value = decodeHtmlEntities(title[1].trim());
  // Strip "Kay & Burton | " prefix.
  const match = value.match(/^Kay\s*&\s*Burton\s*\|\s*(.+)$/i);
  return match ? match[1].trim() : value;
}

function extractPropertyTypeFromUrl(url: string): string | null {
  // .../<area>/<suburb>/<category>/<type>/<id>
  try {
    const parts = new URL(url).pathname.split("/").filter(Boolean);
    // Look for known property-type slugs anywhere in the path.
    const knownTypes = new Set([
      "house", "townhouse", "apartment", "unit", "villa", "studio", "land",
    ]);
    for (const part of parts) {
      if (knownTypes.has(part.toLowerCase())) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function parseKayBurtonDetailHtml(
  html: string,
  url: string,
): Record<string, unknown> {
  const hit: Record<string, unknown> = {};

  const priceMatch = html.match(PRICE_RE);
  if (priceMatch) {
    const display = `$${priceMatch[1].trim()}`;
    const numeric = Number(priceMatch[1].replace(/,/g, ""));
    if (Number.isFinite(numeric) && numeric >= 1000) {
      hit.salePrice = numeric;
      hit.priceDisplay = display;
    }
  }

  const bedMatch = html.match(BEDROOM_RE);
  if (bedMatch) hit.bedrooms = Number(bedMatch[1]);
  const bathMatch = html.match(BATHROOM_RE);
  if (bathMatch) hit.bathrooms = Number(bathMatch[1]);
  const carMatch = html.match(CARSPACE_RE);
  if (carMatch) hit.parking = Number(carMatch[1]);

  const address = extractAddress(html);
  if (address) hit.address = address;

  const propertyType = extractPropertyTypeFromUrl(url);
  if (propertyType) hit.propertyType = propertyType;

  // Only the detail panel's sticker describes this listing. Related cards,
  // menus and article copy can all contain "Sold" for other properties.
  if (/<div\s+class=["'][^"']*info-item\s+details[^"']*["'][^>]*>\s*<div\s+class=["']price["'][^>]*>[\s\S]*?<\/div>\s*<div\s+class=["'][^"']*sticker[^"']*["'][^>]*>\s*Sold\s*<\/div>/i.test(html)) {
    hit.listingType = "sold";
  }

  return hit;
}
